import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'

type BreadcrumbContextValue = {
  pageTitle: string | null
  setPageTitle: (title: string | null) => void
}

const DashboardBreadcrumbContext = createContext<BreadcrumbContextValue | null>(null)

export function DashboardBreadcrumbProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const [pageTitle, setPageTitleState] = useState<string | null>(null)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset the breadcrumb page title on navigation so each page sets its own
    setPageTitleState(null)
  }, [location.pathname])

  const setPageTitle = useCallback((title: string | null) => {
    setPageTitleState(title)
  }, [])

  const value = useMemo(
    () => ({ pageTitle, setPageTitle }),
    [pageTitle, setPageTitle],
  )

  return (
    <DashboardBreadcrumbContext.Provider value={value}>
      {children}
    </DashboardBreadcrumbContext.Provider>
  )
}

export function useDashboardBreadcrumb() {
  const ctx = useContext(DashboardBreadcrumbContext)
  if (!ctx) {
    throw new Error('useDashboardBreadcrumb must be used within DashboardBreadcrumbProvider')
  }
  return ctx
}

/**
 * Optional last-crumb label for the current route (e.g. company or property name on edit pages).
 * Clears on navigation; set when your page loads entity data.
 */
export function useBreadcrumbPageTitle(title: string | null | undefined) {
  const { setPageTitle } = useDashboardBreadcrumb()
  const resolved = title?.trim() ? title.trim() : null

  useEffect(() => {
    if (resolved) {
      setPageTitle(resolved)
    } else {
      setPageTitle(null)
    }
    return () => setPageTitle(null)
  }, [resolved, setPageTitle])
}
